/* eslint-disable no-param-reassign */

/**
 * This is store module that handles search functionalities. It keeps
 * query data sent to search API and returned results. It also manages selected
 * search result.
 */
/**
 * All hew state elements added to this module should also be added
 * to the validation schema at /src/store/storage/schema.js
 */
/**
 * Map layer constants
 */

export const LAYERS = {
    PANORAMAS: 'panoramas',
    TRACKS: 'tracks',
};

/**
 * Mutation names
 */
const mutationNames = {
    SET_CENTER: 'setCenter',
    SET_BOUNDS: 'setBounds',
    SET_LAYER: 'setLayer',
    SET_START_DATE: 'setStartDate',
    SET_END_DATE: 'setEndDate',
};

/**
 * Module state
 */
const state = {
    /**
     * The center of the currently displayed map.The value can be any format
     * accepted by TomTom API.
     * @type {(Object|number[]|string[]|string)}
     */
    center: null,

    /**
     * The bounds of the currently displayed map.
     * @type {Object}
     */
    bounds: null,
    /**
     * Holds currently displayed layer name. It could be either `null`
     * (when no layer is visible), `'tracks'` (when tracks layer is
     * presented) or `'panoramas'` (when panorama dots are displayed).
     * @type (string)
     */
    layer: null,

    /**
     * Holds start date for track filering.
     */
    internalStartDate: new Date(2014, 0, 1),
    /**
     * Holds end date for track filtering.
     */
    internalEndDate: new Date(),
};

/**
 * Module getters
 */
const getters = {
    /**
     * Converts bounds from TomTom API object to an array with 2 points
     * (each point represented as array):
     *  - TL - top left point
     *  - BR - bottom right point
     *  When there is no set bounds `null` is returned.
     * @type {Array}
     */
    boundsArray: ({ bounds }) => {
        if (!bounds) {
            return null;
        }

        const {
            _ne: { lng: lngNE, lat: latNE },
            _sw: { lng: lngSW, lat: latSW },
        } = bounds;
        return [[lngSW, latSW], [lngNE, latNE]];
    },

    startDate: ({ internalStartDate }) => new Date(internalStartDate),
    endDate: ({ internalEndDate }) => new Date(internalEndDate),
};

/**
 * Module mutations. Their names are defined as strings-values from map
 * `mutationNames`.
 */
const mutations = {
    /**
     * Sets center of displayed map. The value can be any format
     * accepted by TomTom API.
     * @param {(Object|number[]|string[]|string)} center
     */
    [mutationNames.SET_CENTER]: (s, center) => {
        s.center = center;
    },

    /**
     * Sets bounds of displayed map.
     * @param {Object} bounds
     */
    [mutationNames.SET_BOUNDS]: (s, bounds) => {
        s.bounds = bounds;
    },

    /**
     * Sets the current layer to given value.
     * @param {string?} layer - The new layer to be displayed, `null` if
     *     extra layer on the map should be hidden.
     */
    [mutationNames.SET_LAYER]: (s, layer) => {
        s.layer = layer;
    },

    /**
     * Sets start date for track filtering.
     * @param {Date?} date - The new start date.
     */
    [mutationNames.SET_START_DATE]: (s, date) => {
        s.internalStartDate = date;
    },
    /**
     * Sets end date for track filtering.
     * @param {Date?} date - The new end date.
     */
    [mutationNames.SET_END_DATE]: (s, date) => {
        s.internalEndDate = date;
    },
};

/**
 * Module actions
 */
const actions = {
    /**
     * Sets given value as map center. The value can be any format
     * accepted by TomTom API.
     * @param {(Object|number[]|string[]|string)} center
     */
    setCenter({ commit }, center) {
        commit(mutationNames.SET_CENTER, center);
    },

    /**
     * Sets given value as map bounds.
     * @param {Object} bounds
     */
    setBounds({ commit }, bounds) {
        commit(mutationNames.SET_BOUNDS, bounds);
    },

    /**
     * Sets visibility of panorama layer.
     * @param {boolean} visible
     */
    setPanoramasVisible({ commit }, visible) {
        commit(mutationNames.SET_PANORAMAS_VISIBLE, visible);
    },

    /**
     * Sets the currently displayed layer.
     * @param {string?} layer - Name of the layer to be displayed.
     */
    setLayer({ commit, dispatch }, layer) {
        commit(mutationNames.SET_LAYER, layer);

        dispatch('track/clearMarker', null, { root: true });
    },

    /**
     * Sets the start and end dates for track filtering.
     */
    setTrackDateRange({ commit, dispatch }, { startDate, endDate }) {
        commit(mutationNames.SET_START_DATE, startDate);
        commit(mutationNames.SET_END_DATE, endDate);

        dispatch('track/clearMarker', null, { root: true });
    },

    /**
     * Decides which action to take on coordinates selection based on
     * currently selected map layer.
     * @param {Object} payload - Coordinates of central point.
     * @param {number} payload.lng - Longitude of center point.
     * @param {number} payload.lat - Latitude of center point.
     * @param {Object} payload.map - Displayed TT Map instance.
     */
    selectCoordinates(
        {
            dispatch,
            state: { layer, internalStartDate, internalEndDate },
            rootState: { polygon: { isDrawingModeActive } },
        },
        { lat, lng, map },
    ) {
        if (layer === LAYERS.PANORAMAS) {
            dispatch('panorama/selectCoordinates', { lat, lng }, { root: true });
        }

        if (layer === LAYERS.TRACKS) {
            if (isDrawingModeActive) {
                dispatch(
                    'polygon/selectCoordinates',
                    {
                        lat,
                        lng,
                        map,
                    },
                    {
                        root: true,
                    },
                );
            } else {
                dispatch(
                    'track/selectCoordinates',
                    {
                        lat,
                        lng,
                        map,
                        startDate: internalStartDate,
                        endDate: internalEndDate,
                    },
                    {
                        root: true,
                    },
                );
            }
        }
    },
};

export default {
    namespaced: true,
    actions,
    getters,
    mutations,
    state,
};
